import { Link } from 'react-router-dom';

export function Privacy() {
  return (
    <section className="section">
      <div className="container privacy-page">
        <div className="section-head reveal">
          <p className="eyebrow">Legal</p>
          <h1>Privacy Policy</h1>
          <p className="privacy-updated">Last updated: April 24, 2026</p>
        </div>

        <div className="privacy-body reveal reveal-delay-1">
          <p>
            This Privacy Policy explains how Expanse Financial Technologies, Inc. ("ExpanseFT," "we," "our,"
            or "us") collects, uses, and discloses information when you visit{' '}
            <a href="https://www.expanseft.com">www.expanseft.com</a> (the "Site"). It is intended to satisfy
            the disclosure requirements of the California Consumer Privacy Act, as amended by the California
            Privacy Rights Act ("CCPA/CPRA"), and to describe our practices for all Site visitors.
          </p>

          <h2>1. Information we collect</h2>
          <ul>
            <li>
              <strong>Information you provide.</strong> When you complete our contact form, we collect your
              name, email address, and the contents of your message. If you email or call us, we receive the
              information you choose to share in those communications.
            </li>
            <li>
              <strong>Information collected automatically.</strong> When you browse the Site, we may collect
              your IP address, browser type and version, operating system, device type, referring URL, pages
              viewed, and the date and time of your visit. This information is captured through server logs
              and, where you consent, through cookies or similar technologies.
            </li>
            <li>
              <strong>Cookies and similar technologies.</strong> See section 6 below for the specific cookies
              we set and their purposes.
            </li>
          </ul>
          <p>
            We do not knowingly collect Social Security numbers, financial account numbers, precise
            geolocation, biometric information, or information from children under sixteen (16) through the
            Site.
          </p>

          <h2>2. How we collect information</h2>
          <p>We collect information:</p>
          <ul>
            <li>directly from you, when you submit our contact form or correspond with us;</li>
            <li>
              automatically from your browser and device as you interact with the Site, through server logs;
              and
            </li>
            <li>
              through cookies and similar technologies placed on your device, subject to the consent choices
              you make through our cookie banner.
            </li>
          </ul>

          <h2>3. How we use information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>operate, secure, and improve the Site;</li>
            <li>respond to your inquiries and provide the information or services you request;</li>
            <li>measure Site usage and performance, if you consent to analytics cookies;</li>
            <li>comply with our legal obligations and enforce our terms; and</li>
            <li>
              prevent fraud and protect the rights, property, or safety of ExpanseFT, our users, or others.
            </li>
          </ul>
          <p>
            We do not use the information we collect for automated decision-making or profiling that
            produces legal or similarly significant effects, and we do not engage in cross-context behavioral
            advertising.
          </p>

          <h2>4. How we share information</h2>
          <p>We may share information with:</p>
          <ul>
            <li>
              <strong>Service providers</strong> who process information on our behalf under contract, such
              as our hosting provider, email relay, and — if enabled — analytics providers. These parties
              are bound by contractual confidentiality and use restrictions.
            </li>
            <li>
              <strong>Legal and safety purposes,</strong> when we believe disclosure is necessary to comply
              with a legal obligation, respond to lawful requests, or protect the rights, property, or
              safety of ExpanseFT, our users, or others.
            </li>
            <li>
              <strong>Business transfers,</strong> in connection with a merger, acquisition, financing, or
              sale of assets, in which case personal information may be one of the transferred assets.
            </li>
          </ul>
          <p>
            <strong>Sale or sharing of personal information.</strong> We do not sell your personal information
            for monetary consideration, and we do not share personal information with third parties for
            cross-context behavioral advertising.{' '}
            <strong>
              In the preceding twelve (12) months, we have not sold or shared the personal information of
              Site visitors, and we have not sold or shared the personal information of any consumer under
              sixteen (16) years of age.
            </strong>{' '}
            If our practices ever change, we will update this Policy and make the opt-out mechanism
            described below available. To record an opt-out now, visit our{' '}
            <Link to="/do-not-sell">Do Not Sell My Personal Information</Link> page.
          </p>

          <h2>5. Your rights</h2>
          <p>California residents have the following rights under the CCPA/CPRA:</p>
          <ul>
            <li>
              <strong>Right to know</strong> what personal information we have collected about you,
              including categories, sources, purposes, and recipients.
            </li>
            <li>
              <strong>Right to access</strong> a copy of the personal information we hold about you.
            </li>
            <li>
              <strong>Right to delete</strong> personal information we have collected from you, subject to
              legal exceptions.
            </li>
            <li>
              <strong>Right to correct</strong> inaccurate personal information.
            </li>
            <li>
              <strong>Right to opt out of sale or sharing.</strong> See our{' '}
              <Link to="/do-not-sell">Do Not Sell My Personal Information</Link> page. We honor the Global
              Privacy Control (GPC) signal as an opt-out.
            </li>
            <li>
              <strong>Right to limit use of sensitive personal information.</strong> We do not collect
              sensitive personal information through the Site, so this right does not currently apply.
            </li>
            <li>
              <strong>Right to non-discrimination</strong> for exercising any of these rights.
            </li>
          </ul>
          <p>
            You may designate an authorized agent to make a request on your behalf. To exercise any of these
            rights, email us at <a href="mailto:privacy@expanseft.com">privacy@expanseft.com</a>. We will
            verify your identity by matching the information you provide against information we already
            hold, and we will respond within the time frame required by law (generally 45 days, which we may
            extend by an additional 45 days when reasonably necessary, with notice to you).
          </p>

          <h2>6. Cookies</h2>
          <p>The Site sets the following cookies:</p>
          <ul>
            <li>
              <code>eft_consent</code> — records your cookie consent choices. Essential. Twelve-month
              expiration.
            </li>
            <li>
              <code>eft_do_not_sell</code> — records your opt-out of sale or sharing. Essential.
              Twelve-month expiration.
            </li>
            <li>
              Analytics, advertising, and functional cookies may be set with your consent. We do not set any
              of these today; if we add them, they will appear here with their purposes and retention.
            </li>
          </ul>
          <p>
            We honor the Global Privacy Control (GPC) signal as an opt-out of sale and sharing. You can
            adjust your preferences at any time via the "Cookie Settings" link in the Site footer.
          </p>

          <h2>7. Retention</h2>
          <p>We retain personal information only for as long as needed for the purpose it was collected:</p>
          <ul>
            <li>
              <strong>Contact form submissions</strong> (name, email, message) — retained for up to twenty-four
              (24) months following the last correspondence in a given thread, then deleted or anonymized.
            </li>
            <li>
              <strong>Server logs</strong> (IP address, user-agent, request metadata) — retained for up to
              ninety (90) days and then purged, except where a longer period is required for security
              investigations or legal obligations.
            </li>
            <li>
              <strong>Consent records</strong> (<code>eft_consent</code>, <code>eft_do_not_sell</code>) —
              twelve (12) months from the date of your most recent choice.
            </li>
            <li>
              <strong>Analytics data</strong>, if you consent to analytics cookies — retained according to
              the analytics provider's default retention; we configure the shortest available retention
              window and disclose the provider here when enabled.
            </li>
          </ul>

          <h2>8. Security</h2>
          <p>
            We use reasonable administrative, technical, and physical safeguards to protect personal
            information. No method of transmission or storage is perfectly secure, however, and we cannot
            guarantee absolute security.
          </p>

          <h2>9. Changes to this policy</h2>
          <p>
            We may update this Policy from time to time. When we do, we will revise the "Last updated" date
            above. Material changes will be highlighted on the Site.
          </p>

          <h2>10. Contact</h2>
          <p>
            Expanse Financial Technologies, Inc.
            <br />
            Email: <a href="mailto:privacy@expanseft.com">privacy@expanseft.com</a>
            <br />
            Phone: <a href="tel:+15166532500">(516) 653 2500</a>
          </p>
        </div>
      </div>
    </section>
  );
}
